import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";

const defaultYear = String(new Date().getFullYear());

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  table: { fontSize: 13 },
  th: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13, backgroundColor: "#f9f9f9", fontWeight: "bold" },
  tdLeft: { border: "1px solid #000", padding: 6, textAlign: "left", fontSize: 13 },
  tdCenter: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdRight: { border: "1px solid #000", padding: 6, textAlign: "right", fontSize: 13 },
};

const formatMoney = (value) => (value && value !== 0 ? formatVndIntegerValue(value) : "");

const buildPrintTemplate = ({ unit, companyAddress, filters, trackedAccount, detailColumns, items, summary }) => {
  const fixedColumns = Array.from({ length: 10 }).map((_, index) => detailColumns[index] || null);
  const bodyRows = items
    .map(
      (item, idx) => `
      <tr>
        <td class="cell center">${idx + 1}</td>
        <td class="cell center">${item.voucherNumber || ""}</td>
        <td class="cell center">${formatDate(item.voucherDate)}</td>
        <td class="cell left">${item.description || ""}</td>
        ${fixedColumns
          .map(
            (column) =>
              `<td class="cell right">${column && Number(item?.detailAmounts?.[column.key] || 0) ? formatMoney(item.detailAmounts[column.key]) : ""}</td>`
          )
          .join("")}
        <td class="cell right"><strong>${item.debitAmount ? formatMoney(item.debitAmount) : ""}</strong></td>
      </tr>`
    )
    .join("");

  return `
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Nhật ký chứng từ số 2 (S04a2-DN)</title>
      <style>
        body { font-family: 'Times New Roman', serif; color: #000; margin: 24px; font-size: 14px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .cell, .head { border: 1px solid #000; padding: 6px; vertical-align: middle; }
        .head { font-weight: 700; text-align: center; background-color: #f9f9f9; }
        table { width: 100%; border-collapse: collapse; }
        .title { text-align: center; margin: 10px 0 16px; }
        .title h1 { margin: 0; font-size: 26px; text-transform: uppercase; }
        .title p { margin: 4px 0; }
        .center { text-align: center; }
        .left { text-align: left; }
        .right { text-align: right; }
        .sign { margin-top: 48px; display: flex; justify-content: space-between; align-items: flex-start; text-align: center; }
        .sign-col { width: 32%; }
        .sign-date-row { min-height: 20px; margin-bottom: 2px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div><strong>Đơn vị:</strong> ${unit || "......................................"}</div>
          <div><strong>Địa chỉ:</strong> ${companyAddress || "......................................"}</div>
        </div>
        <div class="center">
          <div><strong>Mẫu số S04a2-DN</strong></div>
          <div><em>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</em></div>
        </div>
      </div>
      <div class="title">
        <h1>NHẬT KÝ CHỨNG TỪ SỐ 2</h1>
        <p>Ghi Có Tài khoản 112 - Tiền gửi Ngân hàng</p>
        <p>Năm: ${filters.year}</p>
        <p>Tài khoản: ${trackedAccount.accountCode || filters.accountCode}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th class="head" rowspan="2">Số TT</th>
            <th class="head" colspan="2">Chứng từ</th>
            <th class="head" rowspan="2">Diễn giải</th>
            <th class="head" colspan="10">Ghi Nợ các Tài khoản đối ứng</th>
            <th class="head" rowspan="2">Tổng cộng ghi Có TK 112</th>
          </tr>
          <tr>
            <th class="head">Số hiệu</th>
            <th class="head">Ngày tháng</th>
            ${fixedColumns
              .map(
                (column, index) =>
                  `<th class="head">${column?.accountCode || "..."}</th>`
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${bodyRows || '<tr><td class="cell center" colspan="15">Không có dữ liệu</td></tr>'}
          <tr>
            <td class="cell left" colspan="4"><strong>Cộng</strong></td>
            ${fixedColumns
              .map(
                (column) =>
                  `<td class="cell right"><strong>${column ? formatMoney(summary?.totalDetailAmounts?.[column.key] || 0) : ""}</strong></td>`
              )
              .join("")}
            <td class="cell right"><strong>${formatMoney(summary.totalDebitAmount || 0)}</strong></td>
          </tr>
        </tbody>
      </table>
      <div class="sign">
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Người ghi sổ</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Kế toán trưởng</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row"><em>Ngày ...... tháng ...... năm ......</em></div>
          <div><strong>Người đại diện theo pháp luật</strong></div>
          <div><em>(Ký, họ tên, đóng dấu)</em></div>
        </div>
      </div>
    </body>
  </html>`;
};

export default function JournalVoucher2Detail({ baseRoute = "/templates/journal-voucher-2" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [unit, setUnit] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [filters, setFilters] = useState({
    year: defaultYear,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [trackedAccount, setTrackedAccount] = useState({ accountCode: "", accountName: "" });
  const [detailColumns, setDetailColumns] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalDebitAmount: 0,
    totalDetailAmounts: {},
    totalOtherAccountAmount: 0,
    postedToLedgerCount: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    setFilters({
      year: searchParams.get("year") || defaultYear,
      fromDate: searchParams.get("fromDate") || "",
      toDate: searchParams.get("toDate") || "",
      accountCode: searchParams.get("accountCode") || "",
      searchBy: searchParams.get("searchBy") || "",
    });
  }, [location.search]);

  useEffect(() => {
    const init = async () => {
      const response = await CompanyService.getByAuth();
      const payload = response?.data || response || {};
      setCompanyId(payload?._id || "");
      setUnit(payload?.name || payload?.companyName || payload?.unit || "");
      setCompanyAddress(payload?.address || payload?.companyAddress || "");
    };
    init();
  }, []);

  const fixedColumns = useMemo(() => Array.from({ length: 10 }).map((_, index) => detailColumns[index] || null), [detailColumns]);

  const fetchData = useCallback(async () => {
    if (!companyId || !filters.year || !filters.accountCode) return;
    try {
      setLoading(true);
      const response = await GeneralJournalService.getCashPaymentJournal({
        company: companyId,
        year: Number(filters.year),
        accountCode: filters.accountCode,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        searchBy: filters.searchBy || undefined,
        paginate: false,
      });
      const payload = response?.data || response || {};
      setTrackedAccount(payload.trackedAccount || { accountCode: filters.accountCode, accountName: "" });
      setDetailColumns(payload.detailColumns || []);
      setItems(payload.items || []);
      setSummary(payload.summary || {
        totalRows: 0,
        totalDebitAmount: 0,
        totalDetailAmounts: {},
        totalOtherAccountAmount: 0,
        postedToLedgerCount: 0,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Nhật ký Chứng từ số 2");
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(
      buildPrintTemplate({
        unit,
        companyAddress,
        filters,
        trackedAccount,
        detailColumns,
        items,
        summary,
      })
    );
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    Nhật ký Chứng từ số 2 (S04a2-DN)
                  </SoftTypography>
                </SoftBox>
                <SoftBox border="1px solid #000" p={3} sx={{ fontFamily: "'Times New Roman', serif", color: "#000" }}>
                  <SoftBox display="flex" justifyContent="space-between" mb={2}>
                    <SoftBox>
                      <div style={{ fontSize: 14 }}><strong>Đơn vị:</strong> {unit || "......................................"}</div>
                      <div style={{ fontSize: 14 }}><strong>Địa chỉ:</strong> {companyAddress || "......................................"}</div>
                    </SoftBox>
                    <SoftBox textAlign="center" width="48%">
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Mẫu số S04a2-DN</div>
                      <div style={{ fontSize: 14, fontStyle: "italic" }}>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox textAlign="center" mb={2}>
                    <div style={{ fontSize: 28, fontWeight: 700, textTransform: "uppercase" }}>NHẬT KÝ CHỨNG TỪ SỐ 2</div>
                    <div style={{ fontSize: 14, marginTop: 6, fontStyle: "italic" }}>Ghi Có Tài khoản 112 - Tiền gửi Ngân hàng</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>Năm: {filters.year}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      Tài khoản: {trackedAccount.accountCode || filters.accountCode}
                      {trackedAccount.accountName ? ` - ${trackedAccount.accountName}` : ""}
                    </div>
                  </SoftBox>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1280, ...ui.table }}>
                      <thead>
                        <tr>
                          <th style={ui.th} rowSpan={2}>Số TT</th>
                          <th style={ui.th} colSpan={2}>Chứng từ</th>
                          <th style={ui.th} rowSpan={2}>Diễn giải</th>
                          <th style={ui.th} colSpan={10}>Ghi Nợ các Tài khoản đối ứng</th>
                          <th style={ui.th} rowSpan={2}>Tổng cộng ghi Có TK 112</th>
                        </tr>
                        <tr>
                          <th style={ui.th}>Số hiệu</th>
                          <th style={ui.th}>Ngày tháng</th>
                          {fixedColumns.map((column, index) => (
                            <th key={index} style={ui.th}>
                              {column?.accountCode || "..."}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.length > 0 ? (
                          items.map((item, idx) => (
                            <tr key={idx}>
                              <td style={ui.tdCenter}>{idx + 1}</td>
                              <td style={ui.tdCenter}>{item.voucherNumber}</td>
                              <td style={ui.tdCenter}>{formatDate(item.voucherDate)}</td>
                              <td style={ui.tdLeft}>{item.description}</td>
                              {fixedColumns.map((column, cIdx) => (
                                <td key={cIdx} style={ui.tdRight}>
                                  {column && Number(item?.detailAmounts?.[column.key] || 0) ? formatMoney(item.detailAmounts[column.key]) : ""}
                                </td>
                              ))}
                              <td style={{ ...ui.tdRight, fontWeight: "bold" }}>{formatMoney(item.debitAmount)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={15} style={ui.tdCenter}>Không có dữ liệu</td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={4} style={{ ...ui.tdLeft, fontWeight: "bold" }}>Cộng</td>
                          {fixedColumns.map((column, idx) => (
                            <td key={idx} style={{ ...ui.tdRight, fontWeight: "bold" }}>
                              {column ? formatMoney(summary?.totalDetailAmounts?.[column.key] || 0) : ""}
                            </td>
                          ))}
                          <td style={{ ...ui.tdRight, fontWeight: "bold" }}>{formatMoney(summary.totalDebitAmount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </SoftBox>
                <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigate(baseRoute)}>
                    Quay lại
                  </SoftButton>
                  <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                    In Nhật ký S04a2-DN
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
